import { Button, InputField, Select } from '@salt/components';
import cx from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { createUser, editUser, getCustomers } from '../../api/users';

const InviteNewUserModal = ({
  handleInviteNewUserModalClose,
  fetchData,
  user,
  userDetail,
  setInviteLinkOpen,
}) => {
  const [name, setName] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [usernameError, setUsernameError] = useState(undefined);
  const [emailError, setEmailError] = useState(undefined);

  const [customer, setCustomer] = useState(user?.customer || userDetail.customer || '');
  const [customers, setCustomers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(user?.role);

  const fetchCustomersData = useCallback(async () => {
    // setLoading(true);
    try {
      const customersData = await getCustomers();

      setCustomers(customersData.data?.results || []);
    } catch (error) {
      console.error(error.message);
    }
    // setLoading(false);
  }, []);

  useEffect(() => {
    fetchCustomersData();
  }, []);

  const onInvite = useCallback(() => {
    if (user) {
      editUser({ memberId: user.id, username: name, email, role: selectedRole, customer })
        .then(() => {
          fetchData();
          handleInviteNewUserModalClose();
        })
        .catch((err) => {
          console.error('Failed to edit user', err?.response?.data);
          if (err?.response?.data?.error === 'username is already in use.') {
            setUsernameError('Username already in use');
          }
          if (err?.response?.data?.error === 'email is already in use.') {
            setEmailError('Email already in use');
          }
        });
    } else {
      createUser({ username: name, email, role: selectedRole, customer })
        .then((res) => {
          setInviteLinkOpen(res.data?.inviteLink);
          fetchData();
          handleInviteNewUserModalClose();
        })
        .catch((err) => {
          console.error('Failed to create user', err?.response?.data);
          if (err?.response?.data?.error === 'username is already in use.') {
            setUsernameError('Username already in use');
          }
          if (err?.response?.data?.error === 'email is already in use.') {
            setEmailError('Email already in use');
          }
        });
    }
  }, [name, email, selectedRole, user, customer]);

  return (
    <div id="dialog" className="overlay z-[100] fixed top-0 left-0 m-auto w-full h-full flex">
      <button
        type="button"
        className="fixed w-full h-full z-[99] bg-black/30"
        onClick={handleInviteNewUserModalClose}
      />
      <div className="z-[100] w-[583px] react-modal small bg-secondary-light px-8 py-7 bg-white m-auto rounded-lg max-h-[95dvh] overflow-y-scroll">
        <div className="text-xl mb-4">{user ? 'Edit User' : 'Invite new User'}</div>
        {!user && (
          <span className="text-base">
            Invited users will receive an email with onboarding instructions.
          </span>
        )}
        <div className="mt-4">
          <InputField
            value={name}
            labelBackgroundClass="peer-focus:bg-secondary-light peer-[:not(:placeholder-shown)]:bg-secondary-light"
            backgroundClass="bg-secondary-light"
            onChange={(event) => {
              if (event.target.value.length > 0 && !/^[a-z].*/i.test(event.target.value)) {
                setUsernameError('Usernames must start with a letter');
              } else if (
                event.target.value.length > 0 &&
                !/^[a-z][a-z0-9-]*$/i.test(event.target.value)
              ) {
                setUsernameError(
                  'Usernames must only include lowercase letters, numbers or hyphens'
                );
              } else if (
                event.target.value.length > 0 &&
                (event.target.value.length < 8 || event.target.value.length > 15)
              ) {
                setUsernameError('Usernames must be between 8 and 15 characters long');
              } else {
                setUsernameError(undefined);
              }
              setName(event.target.value);
            }}
            placeholder="Username"
            readOnly={user}
            className={user ? 'cursor-not-allowed focus:outline-none opacity-60' : ''}
            // borderClass={user ? 'border-1 border-solid border-secondary-dark' : ''}
          />
          {usernameError && (
            <span className="inline-block text-negative pt-1">{usernameError}</span>
          )}
        </div>
        <div className="mt-4">
          <InputField
            value={email}
            labelBackgroundClass="peer-focus:bg-secondary-light peer-[:not(:placeholder-shown)]:bg-secondary-light"
            backgroundClass="bg-secondary-light"
            onChange={(event) => {
              if (
                event.target.value.length > 0 &&
                !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
                  event.target.value
                )
              ) {
                setEmailError('Invalid email');
                // } else if (otherEmails.includes(event.target.value)) {
                //   setEmailError('A user with this email already exists');
              } else {
                setEmailError(undefined);
              }
              setEmail(event.target.value);
            }}
            placeholder="Email"
          />
          {emailError && <span className="inline-block text-negative pt-1">{emailError}</span>}
        </div>

        {userDetail.role === 'installer' && (
          <div className="mt-4">
            <Select
              onClick={(newVal) => setCustomer(newVal)}
              width="w-full"
              labelBackgroundClass={`group-focus:bg-secondary-light ${customer?.length > 0 ? 'bg-secondary-light' : ''}`}
              backgroundClass="bg-secondary-light"
              placeholder="Select a Customer"
              // readOnly={userDetail.role !== 'installer'}
              renderItems={customers.map((c) => ({
                id: c.id,
                value: c.name,
                render: () => {},
              }))}
              value={customer}
              permScroller={customers.length > 4}
            />
          </div>
        )}

        <div className="text-xl mb-4 mt-8">Role Assignment</div>
        <div className="text-base">
          Assign role permissions to ensure each user sees and performs only what is relevant to
          their responsibilities.
        </div>

        <br />
        <div>
          <button
            type="button"
            onClick={() => setSelectedRole('admin')}
            className={cx(
              'bg-[#E7FCF6] w-full p-4 text-left relative flex items-center my-2 disabled:cursor-not-allowed disabled:opacity-60',
              {
                'border border-solid border-[#00B981]': selectedRole === 'admin',
              }
            )}
            disabled={userDetail.email === user?.email}
          >
            <svg
              width="16"
              height="14"
              viewBox="0 0 16 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_862_976)">
                <path
                  d="M0.262488 1.7557C0.0748868 1.8241 -0.0189136 2.00651 0.00219147 2.21173L0.893295 11.4007C0.916745 11.6287 1.10435 11.8111 1.3623 11.8111H14.6351C14.8696 11.8111 15.0806 11.6287 15.1041 11.4007L15.9952 2.21173C16.0186 2.02932 15.9014 1.84691 15.7372 1.7557C15.5496 1.66449 15.3386 1.7101 15.2213 1.84691L11.7976 5.2443L11.5162 5.51792L8.37388 0.228013C8.28008 0.0912052 8.13938 0 7.97522 0C7.81107 0 7.64692 0.0912052 7.57657 0.228013L4.43426 5.51792L0.776045 1.86971C0.635344 1.7101 0.424293 1.6873 0.260143 1.7557H0.262488Z"
                  fill="#00B981"
                />
                <path
                  d="M1.52662 13.0879C1.26867 13.0879 1.05762 13.2931 1.05762 13.5439C1.05762 13.7947 1.26867 13.9999 1.52662 13.9999H14.4476C14.7056 13.9999 14.9166 13.7947 14.9166 13.5439C14.9166 13.2931 14.7056 13.0879 14.4476 13.0879H1.52662Z"
                  fill="#00B981"
                />
              </g>
              <defs>
                <clipPath id="clip0_862_976">
                  <rect width="16" height="14" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div>
              <span className="text-base block font-bold mx-4">Admin</span>
              <span className="text-[14px] mx-4">Invite and manage users on the system.</span>
            </div>
            {selectedRole === 'admin' && (
              <div className="rounded-full p-3 ml-auto bg-[#00B981]/10">
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.32208 7.99966C3.03264 7.99966 2.74319 7.90699 2.50241 7.72166L0.39944 6.09232C-0.0488184 5.74483 -0.133347 5.09876 0.212452 4.64831C0.558251 4.19786 1.20118 4.11291 1.64944 4.4604L3.24524 5.69592L8.22731 0.326558C8.61409 -0.0904298 9.26214 -0.113596 9.67454 0.275078C10.0895 0.663753 10.11 1.31497 9.72577 1.72939L4.26471 7.61613C4.006 7.86838 3.66532 7.99708 3.32464 7.99708L3.32208 7.99966Z"
                    fill="#00B981"
                  />
                </svg>
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => setSelectedRole('commander')}
            className={cx(
              'bg-[#EFF6FF] w-full p-4 text-left relative flex items-center my-2 disabled:cursor-not-allowed disabled:opacity-60',
              {
                'border border-solid border-[#408BF7]': selectedRole === 'commander',
              }
            )}
            disabled={userDetail.email === user?.email}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.96106 15.6785C8.98557 15.7891 9.0203 15.8956 9.06524 15.998H0.318652C0.142985 15.998 0 15.8546 0 15.6785V12.7869C0 10.4994 1.85676 8.63791 4.13839 8.63791H5.96451C5.90527 8.74235 5.85829 8.84884 5.82152 8.95738C5.78271 9.06387 5.75616 9.17036 5.73778 9.27685C5.61317 9.96903 5.85012 10.6981 6.38325 11.1814V11.2141C5.67241 11.8592 5.48857 12.9364 5.97881 13.7883L6.49559 14.6853C6.65288 14.9576 6.86736 15.187 7.1186 15.357C7.34125 15.5126 7.59454 15.6211 7.86212 15.6764H8.71799C8.78744 15.6621 8.85484 15.6437 8.92225 15.6211C8.93246 15.6273 8.94064 15.6314 8.95085 15.6375L8.96106 15.6764V15.6785ZM15.8938 13.1494L15.379 14.0463C15.232 14.3003 14.9644 14.4457 14.6886 14.4457C14.5722 14.4457 14.4537 14.4211 14.3434 14.3658L14.0125 14.204C13.9921 14.1938 13.9696 14.1897 13.9471 14.1897C13.9124 14.1897 13.8777 14.202 13.8471 14.2245C13.5652 14.4477 13.2526 14.6259 12.9238 14.761C12.8645 14.7836 12.8257 14.8348 12.8237 14.8942L12.7971 15.2628C12.7685 15.6764 12.4213 16 12.0066 16H10.9731C10.8382 16 10.7116 15.9652 10.5993 15.9038C10.4971 15.8485 10.4073 15.7727 10.3398 15.6805C10.25 15.5618 10.1948 15.4205 10.1826 15.2628L10.156 14.8942C10.1519 14.8348 10.1152 14.7836 10.0559 14.761C9.72501 14.6259 9.41861 14.4477 9.13469 14.2245C9.10405 14.1999 9.06728 14.1897 9.03255 14.1897C9.01009 14.1897 8.98966 14.1938 8.96923 14.204L8.63832 14.3658C8.52802 14.4211 8.40955 14.4457 8.29516 14.4457C8.0194 14.4457 7.75182 14.3023 7.60475 14.0484L7.08796 13.1494C6.87961 12.791 6.98787 12.3261 7.33103 12.0927L7.63539 11.8858C7.68237 11.8531 7.70892 11.7957 7.70075 11.7363C7.67624 11.5582 7.66194 11.378 7.66194 11.1977C7.66194 11.0175 7.6742 10.8353 7.70075 10.6592C7.70892 10.5998 7.68237 10.5424 7.63539 10.5097L7.33103 10.3028C6.99808 10.0755 6.88778 9.63113 7.07162 9.27685C7.0757 9.26661 7.08183 9.25637 7.08796 9.24613L7.25546 8.95738L7.43929 8.63791L7.60475 8.34916C7.74978 8.09523 8.01736 7.94983 8.29516 7.94983C8.40955 7.94983 8.52802 7.9744 8.63832 8.02969L8.96923 8.19148C8.98966 8.20172 9.01213 8.20581 9.03255 8.20581C9.06728 8.20581 9.10405 8.19352 9.13469 8.171C9.41657 7.94778 9.72705 7.76961 10.058 7.63446C10.1172 7.61193 10.156 7.56073 10.158 7.50134L10.1846 7.13273C10.2132 6.71906 10.5604 6.39549 10.9751 6.39549H12.0087C12.4213 6.39549 12.7706 6.72111 12.7992 7.13273L12.8257 7.50134C12.8298 7.56073 12.8666 7.61193 12.9258 7.63446C13.2567 7.76961 13.5672 7.94778 13.8491 8.171C13.8797 8.19352 13.9145 8.20581 13.9512 8.20581C13.9717 8.20581 13.9941 8.20172 14.0146 8.19148L14.3455 8.02969C14.4558 7.9744 14.5742 7.94983 14.6907 7.94983C14.9644 7.94983 15.234 8.09318 15.3811 8.34916L15.8958 9.24613C16.1042 9.60451 15.9959 10.0694 15.6528 10.3028L15.3484 10.5097C15.3014 10.5424 15.2749 10.5998 15.283 10.6592C15.3075 10.8373 15.3218 11.0175 15.3218 11.1977C15.3218 11.378 15.3096 11.5602 15.283 11.7363C15.2749 11.7957 15.3014 11.8531 15.3484 11.8858L15.6528 12.0927C15.9959 12.3261 16.1021 12.791 15.8958 13.1494H15.8938ZM13.0361 11.1998C13.0361 10.3458 12.3416 9.64956 11.4899 9.64956C11.1978 9.64956 10.924 9.73147 10.6912 9.87278C10.5727 9.9424 10.4665 10.0305 10.3725 10.1288C10.2377 10.2721 10.1274 10.44 10.0539 10.6243C9.98442 10.8025 9.94357 10.995 9.94357 11.1977C9.94357 11.4005 9.98442 11.595 10.0539 11.7712C10.1295 11.9575 10.2377 12.1254 10.3725 12.2667C10.4665 12.365 10.5727 12.4531 10.6912 12.5227C10.924 12.6661 11.1978 12.7459 11.4899 12.7459C12.3416 12.7459 13.0361 12.0497 13.0361 11.1957V11.1998ZM6.7019 7.04057C8.63832 7.04057 10.2132 5.46167 10.2132 3.52029C10.2132 1.57891 8.63832 0 6.7019 0C4.76548 0 3.1906 1.57891 3.1906 3.52029C3.1906 5.46167 4.76548 7.04057 6.7019 7.04057Z"
                fill="#408BF7"
              />
            </svg>

            <div>
              <span className="text-base block mx-4 font-bold">Commander</span>
              <span className="text-[14px] mx-4">Manage and oversee projects and reports.</span>
            </div>
            {selectedRole === 'commander' && (
              <div className="rounded-full p-3 ml-auto bg-[#408BF7]/10">
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.32208 7.99966C3.03264 7.99966 2.74319 7.90699 2.50241 7.72166L0.39944 6.09232C-0.0488184 5.74483 -0.133347 5.09876 0.212452 4.64831C0.558251 4.19786 1.20118 4.11291 1.64944 4.4604L3.24524 5.69592L8.22731 0.326558C8.61409 -0.0904298 9.26214 -0.113596 9.67454 0.275078C10.0895 0.663753 10.11 1.31497 9.72577 1.72939L4.26471 7.61613C4.006 7.86838 3.66532 7.99708 3.32464 7.99708L3.32208 7.99966Z"
                    fill="#408BF7"
                  />
                </svg>
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => setSelectedRole('operator')}
            className={cx(
              'bg-[#F9FAFC] w-full p-4 text-left relative flex items-center my-2 disabled:cursor-not-allowed disabled:opacity-60',
              {
                'border border-solid border-[#434C58]': selectedRole === 'operator',
              }
            )}
            disabled={userDetail.email === user?.email}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.096 3.52917L13.6278 5.00187C13.5195 5.11013 13.3626 5.15306 13.215 5.11573L11.7039 4.73496C11.4873 4.68083 11.3173 4.51097 11.2631 4.29446L10.8858 2.78256C10.8485 2.6351 10.8933 2.48018 10.9998 2.37192L12.4735 0.904815C12.7425 0.637899 12.6155 0.163798 12.2438 0.089136C10.7756 -0.202045 9.2029 0.219794 8.06911 1.36025C6.74481 2.68363 6.38805 4.58937 6.99136 6.23193L0.814385 11.7494C0.777028 11.7868 0.741539 11.8073 0.711653 11.8446C-0.233478 12.791 -0.24095 14.3309 0.711653 15.2847C1.70161 16.2739 3.3173 16.2291 4.24749 15.1895L9.77633 9.00934C11.4144 9.60663 13.3196 9.24266 14.6365 7.92675C15.7759 6.79376 16.198 5.22399 15.9085 3.76062C15.8356 3.38918 15.3612 3.26226 15.0941 3.53104L15.096 3.52917Z"
                fill="#434C58"
                fillOpacity="0.6"
              />
            </svg>

            <div>
              <span className="text-base block mx-4 font-bold">Operator</span>
              <span className="text-[14px] mx-4">Manage and oversee reports.</span>
            </div>
            {selectedRole === 'operator' && (
              <div className="rounded-full p-3 ml-auto bg-[#434C58]/10">
                <svg
                  width="10"
                  height="8"
                  viewBox="0 0 10 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.32208 7.99966C3.03264 7.99966 2.74319 7.90699 2.50241 7.72166L0.39944 6.09232C-0.0488184 5.74483 -0.133347 5.09876 0.212452 4.64831C0.558251 4.19786 1.20118 4.11291 1.64944 4.4604L3.24524 5.69592L8.22731 0.326558C8.61409 -0.0904298 9.26214 -0.113596 9.67454 0.275078C10.0895 0.663753 10.11 1.31497 9.72577 1.72939L4.26471 7.61613C4.006 7.86838 3.66532 7.99708 3.32464 7.99708L3.32208 7.99966Z"
                    fill="#434C58"
                  />
                </svg>
              </div>
            )}
          </button>
        </div>

        <div>
          <br />
          <Button
            onClick={handleInviteNewUserModalClose}
            type="neutral"
            className="max-w-[146px] h-[36px] ml-auto"
            paddingClassName="px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={onInvite}
            type="positive"
            disabled={
              !name ||
              !email ||
              !selectedRole ||
              !customer ||
              (user?.username === name &&
                user?.role === selectedRole &&
                user?.email === email &&
                user?.customer === customer) ||
              usernameError ||
              emailError
            }
            className="max-w-[146px] h-[36px] ml-auto float-right"
            paddingClassName="px-6"
          >
            {user ? 'Save' : 'Invite'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InviteNewUserModal;
